import { MapPin, Search, Store } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { profileApi, ratingsApi, tutorListingsApi } from '../api/endpoints'
import { Stars } from '../components/profile/RecentRatings'
import { VerifiedBadge } from '../components/VerifiedBadge'
import type { ParticipantCardResponse } from '../types/api'

type Sort = 'top-rated' | 'name' | 'experience'

interface Listed {
  card: ParticipantCardResponse
  averageRating: number | null
  ratingCount: number
}

export function MarketplacePage() {
  const [listed, setListed] = useState<Listed[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [subject, setSubject] = useState('')
  const [sort, setSort] = useState<Sort>('top-rated')

  useEffect(() => {
    let cancelled = false
    tutorListingsApi
      .listLive()
      .then(async (listings) => {
        const cards = await Promise.all(
          listings.map((l) => profileApi.getCard(l.participantId).catch(() => null)),
        )
        const present = cards.filter((c): c is ParticipantCardResponse => c !== null)
        const ratings = await Promise.all(
          present.map((c) => ratingsApi.listForParticipant(c.id).catch(() => [])),
        )
        return present.map((card, i) => ({
          card,
          averageRating: ratings[i].length
            ? ratings[i].reduce((s, r) => s + r.rating, 0) / ratings[i].length
            : null,
          ratingCount: ratings[i].length,
        }))
      })
      .then((rows) => {
        if (!cancelled) setListed(rows)
      })
      .catch(() => {
        if (!cancelled) setListed([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const subjects = useMemo(
    () => Array.from(new Set(listed.flatMap((l) => l.card.subjects ?? []))).sort(),
    [listed],
  )

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return listed
      .filter((l) => !subject || (l.card.subjects ?? []).includes(subject))
      .filter((l) => {
        if (!needle) return true
        return (
          l.card.fullName.toLowerCase().includes(needle)
          || (l.card.serviceType ?? '').toLowerCase().includes(needle)
          || (l.card.subjects ?? []).some((s) => s.toLowerCase().includes(needle))
        )
      })
      .sort((a, b) => {
        if (sort === 'name') return a.card.fullName.localeCompare(b.card.fullName)
        if (sort === 'experience') {
          return (b.card.yearsOfExperience ?? 0) - (a.card.yearsOfExperience ?? 0)
        }
        return (b.averageRating ?? -1) - (a.averageRating ?? -1)
      })
  }, [listed, query, subject, sort])

  return (
    <div>
      <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
        <Store size={18} className="text-sun-500" /> Marketplace
      </h1>
      <p className="text-sm text-ink-500">
        Tutors and learning services, ranked by real ratings from connected learners.
      </p>

      <h2 className="mt-5 font-bold text-ink-900">All tutors</h2>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <label className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input !pl-9"
            placeholder="Search by subject or name..."
            aria-label="Search by subject or name"
          />
        </label>
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          aria-label="Subject"
          className="input sm:w-44"
        >
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          aria-label="Sort"
          className="input sm:w-40"
        >
          <option value="top-rated">Top rated</option>
          <option value="experience">Most experienced</option>
          <option value="name">Name (A-Z)</option>
        </select>
      </div>

      {loading ? (
        <div className="card-pad mt-4 text-sm text-ink-500">Loading listings...</div>
      ) : visible.length === 0 ? (
        <div className="card-pad mt-4 py-10 text-center">
          <Store size={22} className="mx-auto text-ink-300" />
          <p className="mt-3 font-semibold text-ink-900">
            {listed.length === 0 ? 'No tutors are listed yet' : 'No tutors match these filters'}
          </p>
          <p className="mx-auto mt-1 max-w-md text-sm text-ink-500">
            {listed.length === 0
              ? 'Tutors can opt in from their Profile - the first listings appear here after the YellowDots team verifies a provider. Until then, everyone near you is on the map.'
              : 'Try a different subject or clear the search.'}
          </p>
          <Link to="/map" className="btn-ghost mt-4">
            <MapPin size={15} /> YellowDots Map
          </Link>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(({ card, averageRating, ratingCount }) => (
            <div key={card.id} className="card-pad">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sun-400/20 font-bold text-amber-700">
                  {card.fullName.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="break-words font-semibold text-ink-900">{card.fullName}</div>
                  <div className="text-xs text-ink-500">
                    {card.serviceType ?? 'Tutor'}
                    {card.yearsOfExperience != null && ` · ${card.yearsOfExperience} yrs`}
                  </div>
                </div>
              </div>

              {averageRating != null ? (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-ink-500">
                  <Stars rating={averageRating} size={12} />
                  {averageRating.toFixed(1)} ({ratingCount})
                </div>
              ) : (
                <div className="mt-2 text-xs text-ink-400">No ratings yet</div>
              )}

              {card.bio && <p className="mt-2 text-sm text-ink-600">{card.bio}</p>}

              {card.subjects?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {card.subjects.slice(0, 6).map((s) => (
                    <span key={s} className="pill bg-ink-100 text-ink-700">{s}</span>
                  ))}
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                <VerifiedBadge status={card.verificationStatus} />
                {card.feeRangeText && <span>{card.feeRangeText}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
